import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { formatDate, startOfDay } from 'date-fns';

import { Navigate, useTypedParams } from '@sorare/routing';

import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { ANY_SPORT_SCORES_MATCHES_DAY_GAMEID } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import type {
  ScoutingCenterGameIdRedirectPageQuery,
  ScoutingCenterGameIdRedirectPageQueryVariables,
} from './__generated__/index.graphql';

const query = gql`
  query ScoutingCenterGameIdRedirectPageQuery($gameId: ID!) {
    anyGame(id: $gameId) {
      id
      date
    }
  }
` as TypedDocumentNode<
  ScoutingCenterGameIdRedirectPageQuery,
  ScoutingCenterGameIdRedirectPageQueryVariables
>;

export const loaderFn = <
  Params extends {
    gameId: string;
  },
>() =>
  queryLoader<Params>(query, ({ gameId }) => ({
    gameId: `Game:${idFromObject(gameId)}`,
  }));

export const ScoutingCenterGameIdRedirectPage = () => {
  const { gameId } = useTypedParams<{ gameId: string }>();
  const { generateSportContextPath } = useSportContext();
  const { data } = useQueryLoader<ScoutingCenterGameIdRedirectPageQuery>();

  if (!data?.anyGame) {
    return <LoadingIndicator fullHeight />;
  }

  const gameDate = startOfDay(new Date(data.anyGame.date));
  const formattedDate = formatDate(gameDate, 'yyyy-MM-dd');

  return (
    <Navigate
      to={generateSportContextPath(ANY_SPORT_SCORES_MATCHES_DAY_GAMEID, {
        params: {
          day: formattedDate,
          gameId: idFromObject(gameId),
        },
      })}
      replace
    />
  );
};
